/**
 * What can go wrong inside the keypad book.
 *
 * Deliberately not the RCU configuration's issue catalog: a panel problem is not
 * a device-definition problem, and the two must never be reported as each other.
 */
export const KEYPAD_SEVERITY = {
  /** The mapping cannot be written to the device as it stands. */
  BLOCKING: 'blocking',
  /** Worth fixing, but the states that are complete can still be written. */
  ADVISORY: 'advisory',
} as const;

export type KeypadSeverity = (typeof KEYPAD_SEVERITY)[keyof typeof KEYPAD_SEVERITY];

export const KEYPAD_ISSUE = {
  /** An operation named a panel that is not in the book. */
  KEYPAD_NOT_FOUND: 'keypad_not_found',
  /** An operation named a key that panel does not have. */
  KEY_NOT_FOUND: 'key_not_found',
  NAME_REQUIRED: 'name_required',
  NAME_TOO_LONG: 'name_too_long',
  /** Two panels in the book carry the same name. */
  DUPLICATE_NAME: 'duplicate_name',
  /** A panel's keys no longer match the layout its type asks for. */
  KEY_COUNT_MISMATCH: 'key_count_mismatch',
  /** A family with a selectable address has none yet. */
  ADDRESS_NOT_SET: 'address_not_set',
  ADDRESS_OUT_OF_RANGE: 'address_out_of_range',
  /** Another panel of the same family already answers at that address. */
  ADDRESS_TAKEN: 'address_taken',
  /** A family placeable once per RCU already has a panel. */
  FAMILY_ALREADY_PRESENT: 'family_already_present',
  /** A key state is being written with nothing in it. */
  NO_LOADS_IN_STATE: 'no_loads_in_state',
  /** A stored value is outside what its target can do. */
  VALUE_OUT_OF_RANGE: 'value_out_of_range',
  /** A stored value no longer matches the kind of load the target points at. */
  VALUE_KIND_CHANGED: 'value_kind_changed',
  /** A state points at an output the current configuration does not offer. */
  TARGET_MISSING: 'target_missing',
  /** A panel with nothing programmed cannot be written. */
  NOTHING_TO_WRITE: 'nothing_to_write',
  /** The device holds a mapping where this console now drives nothing. */
  DEVICE_LEFTOVER: 'device_leftover',
  UNKNOWN_STATE: 'unknown_state',
} as const;

export type KeypadIssueCode = (typeof KEYPAD_ISSUE)[keyof typeof KEYPAD_ISSUE];

export interface KeypadIssue {
  readonly code: KeypadIssueCode;
  readonly severity: KeypadSeverity;
  readonly message: string;
}

export const keypadIssue = (
  code: KeypadIssueCode,
  severity: KeypadSeverity,
  message: string,
): KeypadIssue => ({ code, severity, message });

/** The mapping cannot be written while this stands. */
export const blocking = (code: KeypadIssueCode, message: string): KeypadIssue =>
  keypadIssue(code, KEYPAD_SEVERITY.BLOCKING, message);

/** Something to look at, which does not stop a write. */
export const advisory = (code: KeypadIssueCode, message: string): KeypadIssue =>
  keypadIssue(code, KEYPAD_SEVERITY.ADVISORY, message);
